/**
 * Нийлүүлэгч — ҮҮСГЭХ · ЗАСАХ · АРХИВЛАХ.
 *
 * ⚠️ Энэ бол нийлүүлэгчийн мөрийг өөрчлөх ЦОРЫН ГАНЦ дэлгэц. Өмнө нь
 *    «Худалдан авалт» ба «Лавлах» ХОЁУЛАА `SupplierForm`-ыг mount
 *    хийдэг байсан тул ажилтан хаанаас нэмэхээ мэдэхгүй байв.
 *    «Худалдан авалт» одоо зөвхөн нийлүүлэгчийн ҮЙЛ АЖИЛЛАГААГ
 *    (аялал, зээл, авсан тоо) харуулна.
 *
 * ⚠️ `includeArchived=true` — архивласныг Ч харуулна, эс тэгвээс
 *    буцааж идэвхжүүлэх ямар ч зам үлдэхгүй.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatQty } from '@boots/domain';
import { api, type Envelope } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { SupplierForm, useSupplierRefresh, type SupplierRow } from '../../components/supplier-form';
import { Button, Empty, ErrorNote, Spinner } from '../../components/ui';
import { DataTable, type Column } from '../../components/data-table';

const TYPE_LABEL: Record<string, string> = {
  INDIVIDUAL: 'Хувь хүн',
  COMPANY: 'Байгууллага',
};

const COLUMNS: Column<SupplierRow>[] = [
  { key: 'code', header: 'Код', cell: (s) => s.code, nowrap: true, width: 'w-[110px]', mobile: 'title' },
  {
    key: 'barcodePrefix',
    header: 'Баркод',
    cell: (s) => s.barcodePrefix ?? <span className="text-alert-ink">оноогоогүй</span>,
    nowrap: true,
    width: 'w-[90px]',
  },
  { key: 'name', header: 'Нэр', cell: (s) => s.name },
  { key: 'type', header: 'Төрөл', cell: (s) => TYPE_LABEL[s.type] ?? s.type },
  { key: 'region', header: 'Бүс', cell: (s) => s.region ?? '—' },
  { key: 'city', header: 'Хот', cell: (s) => s.city ?? '—' },
  { key: 'contact', header: 'Холбоо', cell: (s) => s.contact ?? '—' },
  {
    key: 'state',
    header: 'Төлөв',
    cell: (s) =>
      s.isActive ? (
        <span className="rounded-full bg-accent-100 px-2.5 py-[3px] text-[11.5px] text-accent-800">
          Идэвхтэй
        </span>
      ) : (
        <span className="text-ink-3">Архивласан</span>
      ),
    mobile: 'value',
  },
];

export function SetupSuppliersPage() {
  const { can, readOnly } = useAuth();
  const refresh = useSupplierRefresh();
  const [editing, setEditing] = useState<SupplierRow | 'new' | null>(null);

  // `/ref/suppliers` дээрх `adminOrWarehouse`-тай ижил. Сервер мөн шалгана.
  const canManage = can('WAREHOUSE', 'ADMIN') && !readOnly;

  const q = useQuery({
    queryKey: ['ref-suppliers', 'all'],
    queryFn: () => api<Envelope<SupplierRow[]>>('/ref/suppliers?includeArchived=true'),
  });

  if (q.isPending) return <Spinner />;
  if (q.error) return <ErrorNote>{(q.error as Error).message}</ErrorNote>;

  const rows = q.data.data;
  const active = rows.filter((s) => s.isActive).length;

  return (
    <section className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-semibold">Оросын нийлүүлэгчид</h2>
        <span className="num ml-auto text-[12.5px] text-ink-2">
          Идэвхтэй {formatQty(active)} / {formatQty(rows.length)}
        </span>
        {canManage ? <Button onClick={() => setEditing('new')}>＋ Нийлүүлэгч нэмэх</Button> : null}
      </div>

      <DataTable
        rows={rows}
        columns={COLUMNS}
        rowKey={(s) => s.id}
        onRowClick={canManage ? (s) => setEditing(s) : undefined}
        rowClass={(s) => (s.isActive ? '' : 'opacity-55')}
        empty={<Empty>Нийлүүлэгч бүртгэгдээгүй</Empty>}
      />

      <p className="text-[11.5px] text-ink-3">
        Түүхтэй нийлүүлэгч устгагдахгүй — архивлагдана. Архивласан нийлүүлэгч шинэ аялалд
        сонгогдохгүй ч түүх нь бүтэн үлдэнэ.
      </p>

      {editing ? (
        <SupplierForm
          supplier={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onDone={async () => {
            setEditing(null);
            await refresh();
          }}
        />
      ) : null}
    </section>
  );
}
